import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from 'recharts';
import { fmtMoney } from './format';
import { c, t, sp, r, chart } from '../../theme';

// Reporting categories stacked per cost center.
//
// The overall distribution answers "how much is aged"; this answers "where",
// which is the question a site review actually opens with. Two sites holding
// the same value are a different conversation when one is mostly fresh and the
// other mostly idle, and a single combined bar cannot show that.
//
// Stacked bars are the one form where `chart.categorical` is validated —
// adjacent pairs only, since the series sit in fixed order and only neighbors
// touch. The remainder takes the neutral, so the aged categories carry the only
// saturated color on the chart.
//
// The reporting categories, in the workbook's order. Read from the export's
// columns rather than derived — see inventoryMetrics.CATEGORY for why that
// distinction matters (critical spares are excluded from the aged groups at
// Procurement's request, and recomputing age silently undoes it).
//
// These four PARTITION: every line lands in exactly one, so the stack sums to
// the cost center's on-hand value and its height means something.
//
// 'not_aged' is not "Active Parts", which is what this said until the labels
// were checked against the data. 227 lines that have never once been issued
// carry no category column at all and sit in this segment; calling that active
// is the opposite of what the board is for.
const BANDS = [
  { key: 'not_aged',          label: 'Not Aged',              fill: chart.other },
  { key: 'aged_6_12',         label: 'Aged 6–12 mo',          fill: chart.categorical[3] },
  { key: 'aged_12_plus',      label: 'Aged +12 mo',           fill: chart.categorical[1] },
  { key: 'critical_spare_12', label: 'Critical Spare +12 mo', fill: chart.categorical[6] },
];

// Non-Stock 45+ is NOT in the stack, and this is the whole point of the change.
// It overlaps the aged bands — 255 lines carry a Non-Stock value and an aged
// value at once — so stacking it on top of them would add the same dollars
// twice and make each bar taller than the cost center actually holds.
//
// Drawn as its own bar beside the stack instead. Two bars per cost center reads as
// two measurements, which is what they are, where one stack would have read as
// a single total that happened to be wrong.
const NON_STOCK = {
  key: 'non_stock_45', label: 'Non Stock Aged +45 days', fill: chart.categorical[0],
};

// Past a dozen or so bars the labels collide and the chart stops being
// readable, so the tail is summed into one bar rather than scrolled or
// silently dropped — the total on screen still matches the total on the tiles.
const MAX_BARS = 12;

export default function AgeBandByCostCenter({ rows, nonStockRows, costCenters, onPick }) {
  if (!rows?.length || !costCenters?.length) return null;

  // Yard name rather than cost center code: on a site review the reader knows
  // "Crown MRF", not "440".
  const nameOf = Object.fromEntries(costCenters.map((cc) => [cc.cc_id, cc.name || cc.code]));

  const byCc = new Map();
  const entryFor = (ccId) => {
    if (!byCc.has(ccId)) byCc.set(ccId, { cc_id: ccId, label: nameOf[ccId] || '—', total: 0 });
    return byCc.get(ccId);
  };

  for (const row of rows) {
    const entry = entryFor(row.cc_id);
    entry[row.category] = (entry[row.category] || 0) + Number(row.value || 0);
    entry.total += Number(row.value || 0);
  }

  // Deliberately not added to `total`. That figure orders the bars and is the
  // cost center's on-hand value; folding an overlapping marker into it would sort
  // the chart by a number that is not any real quantity.
  for (const row of nonStockRows || []) {
    entryFor(row.cc_id)[NON_STOCK.key] = Number(row.value || 0);
  }

  const sorted = [...byCc.values()].sort((a, b) => b.total - a.total);
  const head = sorted.slice(0, MAX_BARS);
  const tail = sorted.slice(MAX_BARS);

  if (tail.length) {
    const rest = { cc_id: null, label: `+${tail.length} more`, total: 0 };
    for (const entry of tail) {
      for (const band of [...BANDS, NON_STOCK]) rest[band.key] = (rest[band.key] || 0) + (entry[band.key] || 0);
      rest.total += entry.total;
    }
    head.push(rest);
  }

  return (
    <div>
      <div style={s.head}>
        <div style={s.title}>Value by Age Band per Cost Center</div>
        <div style={s.sub}>
          Which sites are holding the aged stock. Non Stock Aged +45 days is the
          narrower second bar — it overlaps the aged bands, so it sits beside the
          stack rather than in it
          {tail.length ? ` — smallest ${tail.length} combined` : ''}
        </div>
      </div>

      <div style={s.chartWrap}>
        <ResponsiveContainer width="100%" height={330}>
          <BarChart data={head} margin={{ top: 8, right: 12, bottom: 8, left: 8 }}>
            <CartesianGrid vertical={false} stroke={chart.grid} />
            {/* Angled, because yard names are long and twelve of them laid
                flat overlap into each other. interval={0} forces every label
                to render — Recharts otherwise drops alternate ones to make
                room, which silently hides sites from the reader. */}
            <XAxis
              dataKey="label"
              tick={{ fontSize: 10, fill: chart.axisText }}
              axisLine={false} tickLine={false} interval={0}
              angle={-35} textAnchor="end" height={96}
            />
            <YAxis
              tickFormatter={fmtMoney}
              tick={{ fontSize: 10, fill: chart.axisText }}
              axisLine={false} tickLine={false} width={64}
            />
            <Tooltip
              cursor={{ fill: 'rgba(27,42,74,0.05)' }}
              formatter={(v, n) => [fmtMoney(v), n]}
              contentStyle={{ fontSize: '12px', borderRadius: r.lg, border: `1px solid ${chart.tooltipBorder}` }}
            />
            <Legend
              wrapperStyle={{ fontSize: '11px', paddingTop: '6px' }}
              iconType="square" iconSize={9}
            />
            {BANDS.map((band) => (
              <Bar
                key={band.key}
                dataKey={band.key}
                name={band.label}
                stackId="age"
                fill={band.fill}
                isAnimationActive={false}
                onClick={onPick ? (d) => d.cc_id && onPick(d.cc_id) : undefined}
                style={onPick ? { cursor: 'pointer' } : undefined}
              />
            ))}
            {/* A second stackId, so Recharts puts this beside the stack rather
                than on top of it. Same chart, separate measurement — see the
                note on NON_STOCK. */}
            <Bar
              key={NON_STOCK.key}
              dataKey={NON_STOCK.key}
              name={NON_STOCK.label}
              stackId="marker"
              fill={NON_STOCK.fill}
              isAnimationActive={false}
              onClick={onPick ? (d) => d.cc_id && onPick(d.cc_id) : undefined}
              style={onPick ? { cursor: 'pointer' } : undefined}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

const s = {
  head:      { padding: `${sp[4]} ${sp[5]} 0`, display: 'flex', alignItems: 'baseline', gap: sp[3], flexWrap: 'wrap' },
  title:     { ...t.h3, color: c.navy },
  sub:       { ...t.micro, color: c.textFaint },
  chartWrap: { padding: `${sp[2]} ${sp[5]} ${sp[3]}` },
};
